// Demo Studio — the prototype's side of the portal's embedded hotspot editor.
//
// Activated by `?embed=1&tourId=<uuid>`. In this mode the viewer is a panel
// inside login.imersivo3d.com/admin/…/tour/editor rather than a standalone
// demo, which changes three things:
//
//   1. The RoomsConfig arrives over postMessage instead of being the bundled
//      src/data/rooms.json. Panorama URLs in it are short-lived signed URLs
//      into the portal's private bucket, so nothing has to be committed to
//      git before an admin can place hotspots.
//   2. Every edit is pushed back to the parent, which owns persistence. The
//      "Exportar rooms.json" download is meaningless here and is hidden.
//   3. Selecting a hotspot tells the parent, so the portal can render a
//      richer inspector beside the iframe.
//
// The event names below are VENDORED from `packages/shared/src/events.ts` in
// the imersivo-studio repo, which is authoritative. This repo does not depend
// on that package today (two repos, one contract — see the plan's risk list);
// publishing it is a later cleanup. If you change a string here, change it
// there in the same commit or the bridge goes silent with no error.

// Explicit `/index.ts`, not the directory: tests/tour-embed.test.ts loads this
// module under `node --test`, whose resolver does not do directory imports and
// does not add extensions. A bare '../i18n' fails there and nowhere else.
import { isLocale, type Locale } from '../i18n/index.ts';
import type { Hotspot, RoomsConfig } from './types';

export const PARENT_TO_IFRAME = {
  tourConfigSet: 'imersivo:tour:config-set',
  roomSelect: 'imersivo:tour:room-select',
  lookSet: 'imersivo:tour:look-set',
  tourAssetPicked: 'imersivo:tour:asset-picked',
} as const;

export const IFRAME_TO_PARENT = {
  ready: 'imersivo:ready',
  error: 'imersivo:error',
  tourConfigChanged: 'imersivo:tour:config-changed',
  hotspotSelected: 'imersivo:tour:hotspot-selected',
  tourAssetRequested: 'imersivo:tour:asset-request',
} as const;

/** Asset kinds the editor can ask the portal for. Mirrors TOUR_ASSET_KINDS. */
export type TourAssetKind = 'info-image';

/** What the portal sends back. `assetPath: null` = cleared or cancelled. */
export interface TourAssetPicked {
  requestId: string;
  hotspotId: string;
  assetPath: string | null;
  url: string | null;
}

export type TourConfigChangeReason =
  | 'hotspot-added'
  | 'hotspot-changed'
  | 'hotspot-deleted'
  | 'hotspot-moved'
  | 'compass'
  // Rotates one variant's panorama to match the room's obra capture
  // (Variant.yawOffsetDeg). Distinct from 'compass', which rotates the floor
  // plan instead. Vendored from the portal's packages/shared/src/events.ts —
  // add to both or the bridge goes silently untyped on one side.
  | 'panorama-yaw'
  | 'manual';

/** Parsed form of a parent→iframe message. `null` = not for us. */
export type ParentMessage =
  | { kind: 'config'; tourId: string; config: RoomsConfig; locale?: Locale }
  | { kind: 'roomSelect'; roomId: string }
  | { kind: 'lookSet'; yaw: number; pitch: number }
  | { kind: 'assetPicked'; picked: TourAssetPicked };

/**
 * Decide whether a message origin may drive this editor.
 *
 * An empty allowlist means "any origin", which is the local-dev default: the
 * portal runs on http://localhost:3000 and the viewer on https://localhost:5174,
 * and hard-coding that pair would make the dev setup a build-time concern. In
 * production `VITE_EMBED_PARENT_ORIGINS` is set, and then a page that frames
 * the editor without being on the list can neither read the config nor push
 * one in.
 */
export function isOriginAllowed(origin: string, allowlist: readonly string[]): boolean {
  if (allowlist.length === 0) return true;
  return allowlist.includes(origin);
}

/**
 * Read VITE_EMBED_PARENT_ORIGINS. Cast through a local shape rather than
 * relying on vite/client types — matches src/app/apiClient.ts, and keeps this
 * module importable from the Node test runner, where import.meta.env is absent.
 */
function readAllowlistEnv(): string | undefined {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
  return env.VITE_EMBED_PARENT_ORIGINS;
}

/** Split the comma-separated VITE_EMBED_PARENT_ORIGINS value. */
export function parseOriginAllowlist(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim().replace(/\/$/, ''))
    .filter(Boolean);
}

/**
 * Validate a parent→iframe message.
 *
 * Returns null for anything unrecognised rather than throwing: the portal page
 * hosting this iframe also runs Next.js dev tooling and browser extensions,
 * both of which postMessage freely, so unknown traffic is the normal case and
 * must stay silent.
 */
export function parseParentMessage(data: unknown): ParentMessage | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as { type?: unknown; payload?: unknown };
  if (typeof msg.type !== 'string') return null;
  const payload = (msg.payload ?? {}) as Record<string, unknown>;

  if (msg.type === PARENT_TO_IFRAME.tourConfigSet) {
    const config = payload.config;
    const tourId = payload.tourId;
    if (typeof tourId !== 'string' || !tourId) return null;
    // A config with no rooms would boot the viewer into a permanent loading
    // screen; reject it here so the parent's error path fires instead.
    if (!config || typeof config !== 'object') return null;
    const rooms = (config as { rooms?: unknown }).rooms;
    if (!Array.isArray(rooms) || rooms.length === 0) return null;
    // Optional: the parent may name the language its own chrome is in, which
    // outranks `?lang=` because the iframe is built once and the admin can
    // switch the portal's language without it being recreated.
    const locale = isLocale(payload.locale) ? payload.locale : undefined;
    return { kind: 'config', tourId, config: config as RoomsConfig, ...(locale ? { locale } : {}) };
  }

  if (msg.type === PARENT_TO_IFRAME.roomSelect) {
    const roomId = payload.roomId;
    if (typeof roomId !== 'string' || !roomId) return null;
    return { kind: 'roomSelect', roomId };
  }

  if (msg.type === PARENT_TO_IFRAME.lookSet) {
    const yaw = payload.yaw;
    const pitch = payload.pitch;
    if (typeof yaw !== 'number' || !Number.isFinite(yaw)) return null;
    if (typeof pitch !== 'number' || !Number.isFinite(pitch)) return null;
    return { kind: 'lookSet', yaw, pitch };
  }

  if (msg.type === PARENT_TO_IFRAME.tourAssetPicked) {
    const requestId = payload.requestId;
    const hotspotId = payload.hotspotId;
    if (typeof requestId !== 'string' || !requestId) return null;
    if (typeof hotspotId !== 'string' || !hotspotId) return null;
    // Both nullable together: a cancel and a clear look identical on the wire,
    // and both mean "this hotspot ends up with no picture".
    const assetPath = typeof payload.assetPath === 'string' && payload.assetPath ? payload.assetPath : null;
    const url = typeof payload.url === 'string' && payload.url ? payload.url : null;
    return { kind: 'assetPicked', picked: { requestId, hotspotId, assetPath, url } };
  }

  return null;
}

/** The payload shape the parent receives on every hotspot selection. */
export interface HotspotSelectedPayload {
  roomId: string;
  hotspotId: string;
  type: string;
  label: string;
  yaw: number;
  pitch: number;
}

export function hotspotSelectedPayload(roomId: string, hotspot: Hotspot): HotspotSelectedPayload {
  return {
    roomId,
    hotspotId: hotspot.id,
    type: hotspot.type,
    label: hotspot.label,
    yaw: hotspot.yaw,
    pitch: hotspot.pitch,
  };
}

export function isTourEmbedMode(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('embed') === '1';
}

export function getEmbedTourId(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('tourId');
}

/** How long to wait for the parent's config before giving up and erroring. */
const CONFIG_TIMEOUT_MS = 20_000;
/**
 * How often to re-announce `ready` until the parent answers with a config.
 *
 * The handshake is one-shot in both directions and the ordering is NOT ours to
 * control. The portal ships this iframe inside its server-rendered HTML, so the
 * browser starts loading this app while the parent's own React bundle is still
 * downloading — and this bundle is the smaller of the two and usually cached,
 * while the portal's chunk names change on every deploy. Win that race and the
 * single `ready` lands before the parent has installed its message listener:
 * nobody ever sends a config, and 20 s later the operator reads "O portal não
 * enviou a configuração do tour" about a portal that was merely not listening
 * yet. Repeating costs one empty message per interval and removes the
 * assumption entirely — the parent treats a second `ready` as the first.
 */
const READY_RETRY_MS = 500;
/** Coalescing window for config pushes — a yaw drag fires on every frame. */
const CONFIG_DEBOUNCE_MS = 600;
/**
 * How long to wait for the parent to answer an asset request.
 *
 * Generous, because the operator is inside a file dialog and then an upload:
 * finding the photo alone can take a minute. It exists only so a parent that
 * never answers — an old portal build with no picker — leaves the editor's
 * button usable again instead of disabled forever.
 */
const ASSET_REQUEST_TIMEOUT_MS = 5 * 60_000;

export class TourEmbedBridge {
  private readonly allowlist: string[];
  private readonly expectedTourId: string | null;
  /** Origin of the parent that sent the config. Replies go here and nowhere
   *  else — the config carries signed URLs, so `*` would hand them to any
   *  page that happens to frame this one. */
  private parentOrigin: string | null = null;
  private tourId: string | null = null;
  private configResolve: ((config: RoomsConfig) => void) | null = null;
  private parentLocale: Locale | null = null;
  private configTimer: ReturnType<typeof setTimeout> | null = null;
  /** Re-announces `ready` until the parent answers — see READY_RETRY_MS. */
  private readyTimer: ReturnType<typeof setInterval> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pending: { config: RoomsConfig; reason: TourConfigChangeReason } | null = null;
  private roomSelectCb: ((roomId: string) => void) | null = null;
  private lookSetCb: ((yaw: number, pitch: number) => void) | null = null;
  private started = false;
  /** Outstanding asset requests, by requestId. More than one can be live: the
   *  operator can open the picker, close it, and click a second hotspot before
   *  the first answer arrives. */
  private assetRequests = new Map<
    string,
    { resolve: (picked: TourAssetPicked) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private assetRequestSeq = 0;

  constructor(opts: { tourId?: string | null; allowedOrigins?: readonly string[] } = {}) {
    this.expectedTourId = opts.tourId ?? getEmbedTourId();
    this.allowlist =
      opts.allowedOrigins !== undefined
        ? [...opts.allowedOrigins]
        : parseOriginAllowlist(readAllowlistEnv());
  }

  private readonly onMessage = (ev: MessageEvent): void => {
    if (!isOriginAllowed(ev.origin, this.allowlist)) return;
    const parsed = parseParentMessage(ev.data);
    if (!parsed) return;
    switch (parsed.kind) {
      case 'config': {
        // Ignore a config for a different tour: the operator may have two
        // editor tabs open, and applying the wrong one would send that tour's
        // hotspots back under this tour's id.
        if (this.expectedTourId && parsed.tourId !== this.expectedTourId) return;
        // The parent is listening and has answered: stop asking. Done here
        // rather than in resolveConfig, which no-ops when nothing is waiting.
        this.stopAnnouncing();
        this.parentOrigin = ev.origin;
        this.tourId = parsed.tourId;
        if (parsed.locale) this.parentLocale = parsed.locale;
        this.resolveConfig(parsed.config);
        break;
      }
      case 'roomSelect':
        this.roomSelectCb?.(parsed.roomId);
        break;
      case 'lookSet':
        this.lookSetCb?.(parsed.yaw, parsed.pitch);
        break;
      case 'assetPicked': {
        const entry = this.assetRequests.get(parsed.picked.requestId);
        // An answer to a request we never made, or already timed out, is
        // dropped. Applying it would write an image onto whichever hotspot the
        // operator has since selected.
        if (!entry) break;
        this.assetRequests.delete(parsed.picked.requestId);
        clearTimeout(entry.timer);
        entry.resolve(parsed.picked);
        break;
      }
    }
  };

  /** Install the listener and announce readiness. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('message', this.onMessage);
    this.announceReady();
    // Keep announcing until a config arrives. Bounded by the same deadline the
    // caller is waiting on, so a page opened with ?embed=1 and no portal at all
    // stops talking to `*` when it gives up rather than for the tab's lifetime.
    const deadline = Date.now() + CONFIG_TIMEOUT_MS;
    this.readyTimer = setInterval(() => {
      if (Date.now() >= deadline) {
        this.stopAnnouncing();
        return;
      }
      this.announceReady();
    }, READY_RETRY_MS);
  }

  /** `ready` is the one message sent to `*`: it carries nothing, and until a
   *  parent answers we do not know which origin to trust. */
  private announceReady(): void {
    window.parent?.postMessage({ type: IFRAME_TO_PARENT.ready, payload: { embed: true } }, '*');
  }

  private stopAnnouncing(): void {
    if (this.readyTimer) clearInterval(this.readyTimer);
    this.readyTimer = null;
  }

  /** True while the frame is still asking the parent to hand over a config. */
  isAnnouncing(): boolean {
    return this.readyTimer !== null;
  }

  /**
   * Resolve with the config the parent pushes. Rejects on timeout so the
   * caller can render a real message instead of an eternal loading screen —
   * the most likely cause is a parent that never mounted the bridge.
   */
  waitForConfig(timeoutMs = CONFIG_TIMEOUT_MS): Promise<RoomsConfig> {
    return new Promise<RoomsConfig>((resolve, reject) => {
      this.configResolve = resolve;
      this.configTimer = setTimeout(() => {
        this.configTimer = null;
        this.configResolve = null;
        this.stopAnnouncing();
        reject(new Error('embed: parent sent no tour config'));
      }, timeoutMs);
    });
  }

  /**
   * The language the parent said it is rendering in, if it said.
   *
   * Only meaningful after waitForConfig() resolves — it arrives on the same
   * message. Absent for a parent that predates the `locale` field, in which
   * case the viewer's own ?lang= / storage / navigator order applies.
   */
  getParentLocale(): Locale | null {
    return this.parentLocale;
  }

  private resolveConfig(config: RoomsConfig): void {
    if (!this.configResolve) return;
    if (this.configTimer) clearTimeout(this.configTimer);
    this.configTimer = null;
    const resolve = this.configResolve;
    this.configResolve = null;
    resolve(config);
  }

  onRoomSelect(cb: (roomId: string) => void): void {
    this.roomSelectCb = cb;
  }

  onLookSet(cb: (yaw: number, pitch: number) => void): void {
    this.lookSetCb = cb;
  }

  /**
   * Queue a config push. Debounced because a drag-to-reposition rewrites
   * yaw/pitch on every pointermove — sending each one would put a few hundred
   * full-config messages (and, if the parent autosaves, database writes) behind
   * a single gesture.
   */
  notifyConfigChanged(config: RoomsConfig, reason: TourConfigChangeReason): void {
    this.pending = { config, reason };
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.flush();
    }, CONFIG_DEBOUNCE_MS);
  }

  /** Send any queued config immediately (the "Salvar no portal" button). */
  flush(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    const pending = this.pending;
    if (!pending) return;
    this.pending = null;
    this.post(IFRAME_TO_PARENT.tourConfigChanged, {
      tourId: this.tourId,
      config: pending.config,
      reason: pending.reason,
    });
  }

  notifyHotspotSelected(roomId: string, hotspot: Hotspot): void {
    this.post(IFRAME_TO_PARENT.hotspotSelected, hotspotSelectedPayload(roomId, hotspot));
  }

  /**
   * Ask the parent for an image to attach to a hotspot.
   *
   * Resolves when the operator picks, clears or cancels — `assetPath: null`
   * covers the last two, because both end with the hotspot having no picture.
   * Rejects only if there is no parent to ask or it never answers, which is
   * the caller's cue to re-enable its control and say nothing happened.
   *
   * The upload itself belongs to the portal: it has the session, the signed
   * upload URL and the project_assets row that lets the file be re-signed
   * after this URL expires. This side never talks to Supabase.
   */
  requestAsset(opts: {
    kind: TourAssetKind;
    roomId: string;
    hotspotId: string;
    currentAssetPath?: string | null;
  }): Promise<TourAssetPicked> {
    if (!this.parentOrigin) {
      return Promise.reject(new Error('embed: no parent to request an asset from'));
    }
    this.assetRequestSeq += 1;
    const requestId = `asset-${this.assetRequestSeq}`;
    return new Promise<TourAssetPicked>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.assetRequests.delete(requestId);
        reject(new Error('embed: parent did not answer the asset request'));
      }, ASSET_REQUEST_TIMEOUT_MS);
      this.assetRequests.set(requestId, { resolve, timer });
      this.post(IFRAME_TO_PARENT.tourAssetRequested, {
        requestId,
        kind: opts.kind,
        roomId: opts.roomId,
        hotspotId: opts.hotspotId,
        currentAssetPath: opts.currentAssetPath ?? null,
      });
    });
  }

  notifyError(message: string): void {
    this.post(IFRAME_TO_PARENT.error, { message });
  }

  /** True once the parent has handed over a config. */
  isConnected(): boolean {
    return this.parentOrigin !== null;
  }

  destroy(): void {
    if (this.configTimer) clearTimeout(this.configTimer);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.stopAnnouncing();
    this.configTimer = null;
    this.debounceTimer = null;
    this.configResolve = null;
    for (const entry of this.assetRequests.values()) clearTimeout(entry.timer);
    this.assetRequests.clear();
    window.removeEventListener('message', this.onMessage);
    this.started = false;
  }

  private post(type: string, payload: unknown): void {
    // Before a parent has identified itself there is nowhere safe to send: an
    // error message would go to `*`, and every other payload carries either the
    // config or the operator's edits.
    if (!this.parentOrigin) return;
    window.parent?.postMessage({ type, payload }, this.parentOrigin);
  }
}
